// Route gate for the employee area's module surfaces. The server refuses the
// same surfaces' reads; this keeps a switched-off module from rendering at
// all when its URL is opened directly.
import { redirect } from "@tanstack/react-router";
import type { ClientModules } from "@pima/web-api/client";
import { fetchMyClientModules } from "@/lib/api/web/own-client";

export type EmployeeSurface = "measures" | "online-courses" | "documents";

type EmployeeModules = Pick<
  ClientModules,
  "measure_center_enabled" | "online_courses_enabled" | "documents_enabled"
>;

const SURFACES: readonly {
  readonly surface: EmployeeSurface;
  readonly path: string;
  readonly enabled: (modules: EmployeeModules) => boolean;
}[] = [
  {
    surface: "measures",
    path: "",
    enabled: (modules) => modules.measure_center_enabled,
  },
  {
    surface: "online-courses",
    path: "/online-courses",
    enabled: (modules) => modules.online_courses_enabled,
  },
  {
    surface: "documents",
    path: "/documents",
    enabled: (modules) => modules.documents_enabled,
  },
];

/**
 * Where an employee lands: their assigned measures, or else the first other
 * surface their employer offers, in menu order. With every module switched
 * off it is their settings, which no module switch governs.
 */
export function employeeHomePath(
  userId: string,
  modules: EmployeeModules,
): string {
  const first = SURFACES.find(({ enabled }) => enabled(modules));
  return `/employee/${userId}${first ? first.path : "/settings"}`;
}

/**
 * `beforeLoad` for an employee surface: an employee whose employer has the
 * surface's module switched off is sent to their employee home instead.
 * Modules that cannot be read leave the employee on the page they opened:
 * the server refuses a switched-off module's reads itself, and the side
 * navigation already says when it could not load the modules.
 */
export async function guardEmployeeSurface(
  surface: EmployeeSurface,
  userId: string,
): Promise<void> {
  const modules = await fetchMyClientModules().catch(() => null);
  if (modules === null) return;
  const entry = SURFACES.find((candidate) => candidate.surface === surface);
  if (entry?.enabled(modules)) return;
  throw redirect({ to: employeeHomePath(userId, modules), replace: true });
}
